import config from '../../config';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Box, Grid, Paper, Alert, Snackbar, useTheme } from '@mui/material';

import Welcome from './Welcome';
import EmailList from './EmailList';
import EmailView from './EmailView';
import SearchBar from './SearchBar';
import LoadingSkeleton from './LoadingSkeleton';
import ComingSoonLoader from './ComingSoonLoader';
import './ComingSoonLoader.css';

function MailinatorApp() {
  const theme = useTheme();
  const [inboxName, setInboxName] = useState('');
  const [currentInbox, setCurrentInbox] = useState('');
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showLoader, setShowLoader] = useState(true);

  const handleUnlock = () => {
    setShowLoader(false);
  };

  const POLLING_INTERVAL = 5000;

  const fetchEmails = useCallback(async (inbox, suppressLoading = false) => {
    if (!inbox) return;

    if (!suppressLoading) setLoading(true);
    setError(null);

    try {
      const encodedInboxName = encodeURIComponent(inbox.toLowerCase());
      const response = await axios.get(`${config.API_BASE_URL}/inbox/${encodedInboxName}`);
      setEmails(response.data);
      if (!currentInbox || currentInbox.toLowerCase() !== inbox.toLowerCase()) {
        setCurrentInbox(inbox);
      }
    } catch (err) {
      console.error('Error fetching emails:', err);
      setError('Failed to fetch emails. Make sure the backend server is running and accessible.');
      setEmails([]);
    } finally {
      if (!suppressLoading) setLoading(false);
    }
  }, [currentInbox]);

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch (e) {
      return isoString;
    }
  };

  const getTimeRemaining = (expiresAtIsoString) => {
    if (!expiresAtIsoString) return '';
    const expirationDate = new Date(expiresAtIsoString);
    const now = new Date();
    const diffMs = expirationDate.getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'Expired';
    }

    const minutes = Math.floor(diffMs / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    const parts = [];
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || minutes === 0) parts.push(`${seconds}s`);

    return `Expires in ${parts.join(' ')}`;
  };

  const getEmailSnippet = (body) => {
    if (!body) return '(No content)';
    const snippet = body.trim().split('\n')[0];
    if (snippet.length > 100) {
      return snippet.substring(0, 97) + '...';
    }
    return snippet;
  };

  useEffect(() => {
    let intervalId;
    if (currentInbox) {
      fetchEmails(currentInbox);

      intervalId = setInterval(() => {
        fetchEmails(currentInbox, true);
      }, POLLING_INTERVAL);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentInbox, fetchEmails]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (inboxName.trim()) {
      setCurrentInbox(inboxName.trim());
      setSelectedEmail(null);
      setSnackbarOpen(false);
    }
  };

  const handleEmailClick = async (email) => {
    setSelectedEmail(email);
    if (!email.is_read) {
      try {
        const updatedEmails = emails.map(e =>
          e.id === email.id ? { ...e, is_read: true } : e
        );
        setEmails(updatedEmails);
        setSelectedEmail({ ...email, is_read: true });

        await axios.patch(`${config.API_BASE_URL}/inbox/${currentInbox.toLowerCase()}/${email.id}/read`, { is_read: true });
      } catch (err) {
        console.error('Error marking email as read:', err);
      }
    }
  };

  const handleBackToInbox = () => {
    setSelectedEmail(null);
  };

  const generateRandomInboxName = () => {
    const randomString = Math.random().toString(36).substring(2, 10);
    const generatedName = `${randomString}@${config.APP_DOMAIN}`;

    setInboxName(generatedName);
    setCurrentInbox(generatedName);
    setSelectedEmail(null);
  };

  const handleDeleteInbox = async () => {
    if (!currentInbox || !window.confirm(`Are you sure you want to clear inbox "${currentInbox}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSnackbarOpen(false);

    try {
      const encodedInboxName = encodeURIComponent(currentInbox.toLowerCase());
      await axios.delete(`${config.API_BASE_URL}/inbox/${encodedInboxName}`);
      setEmails([]);
      setSnackbarMessage(`Inbox "${currentInbox}" cleared successfully!`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error clearing inbox:', err);
      setError('Failed to clear inbox.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyInboxName = () => {
    if (currentInbox) {
      navigator.clipboard.writeText(`${currentInbox}`)
        .then(() => {
          setSnackbarMessage(`Copied "${currentInbox}" to clipboard!`);
          setSnackbarOpen(true);
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
          setSnackbarMessage('Failed to copy inbox name.');
          setSnackbarOpen(true);
        });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleToggleReadStatus = async () => {
    if (!selectedEmail || !currentInbox) return;

    const newReadStatus = !selectedEmail.is_read;
    try {
      setSelectedEmail(prev => ({ ...prev, is_read: newReadStatus }));
      setEmails(prev => prev.map(e =>
        e.id === selectedEmail.id ? { ...e, is_read: newReadStatus } : e
      ));

      await axios.patch(`${config.API_BASE_URL}/inbox/${currentInbox.toLowerCase()}/${selectedEmail.id}/read`, { is_read: newReadStatus });

      setSnackbarMessage(`Email marked as ${newReadStatus ? 'read' : 'unread'}.`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error toggling read status:', err);
      setSnackbarMessage('Failed to update read status.');
      setSnackbarOpen(true);
      setSelectedEmail(prev => ({ ...prev, is_read: !newReadStatus }));
      setEmails(prev => prev.map(e =>
        e.id === selectedEmail.id ? { ...e, is_read: !newReadStatus } : e
      ));
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {showLoader && <ComingSoonLoader onUnlock={handleUnlock} theme={theme} />}
      <SearchBar
        inboxName={inboxName}
        setInboxName={setInboxName}
        handleSearch={handleSearch}
        generateRandomInboxName={generateRandomInboxName}
        loading={loading}
        currentInbox={currentInbox}
        handleCopyInboxName={handleCopyInboxName}
        handleDeleteInbox={handleDeleteInbox}
        emailCount={emails.length}
      />

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      )}

      <Grid container spacing={0} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid item xs={12} md={4} sx={{
          height: '100%',
          borderRight: { md: `1px solid ${theme.palette.divider}` }
        }}>
          <Paper elevation={0} sx={{ height: '100%', overflowY: 'auto', borderRadius: 0 }}>
            {loading ? (
              <LoadingSkeleton />
            ) : (
              <EmailList
                emails={emails}
                selectedEmail={selectedEmail}
                handleEmailClick={handleEmailClick}
                formatDate={formatDate}
                getEmailSnippet={getEmailSnippet}
              />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={8} sx={{ height: '100%', overflowY: 'auto' }}>
          <Paper elevation={0} sx={{ height: '100%', p: 2, borderRadius: 0 }}>
            {selectedEmail ? (
              <EmailView
                selectedEmail={selectedEmail}
                handleBackToInbox={handleBackToInbox}
                handleToggleReadStatus={handleToggleReadStatus}
                formatDate={formatDate}
                getTimeRemaining={getTimeRemaining}
              />
            ) : (
              <Welcome currentInbox={currentInbox} theme={theme} />
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default MailinatorApp;

